import { useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ProductList, useProducts, AddToSetModal, AddToCategoryModal, EditProductModal, type ProductFilters } from '@modules/products';
import { useCategories } from '@modules/categories';
import { useSections } from '@modules/sections';
import { Breadcrumbs, type BreadcrumbItem, BulkActionsSelect, FiltersPopover, type ProductFiltersValues } from '@components';
import { Input } from '@shared/ui';
import { useDebounce } from '@shared/hooks';
import '@shared/styles/page.css';
import './CategoryProductsPage.css';
export default function CategoryProductsPage() {
    const location = useLocation();
    const { categoryId, sectionId } = useParams<{ categoryId: string; sectionId?: string }>();
    const categoryIdNum = categoryId ? parseInt(categoryId, 10) : undefined;
    const isPopular = location.pathname.includes('/sections/popular/');
    const isWithoutSections = location.pathname.includes('/sections/without-sections/');
    const sectionIdNum = sectionId && !isPopular && !isWithoutSections ? parseInt(sectionId, 10) : undefined;
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
    const [isAddToSetModalOpen, setIsAddToSetModalOpen] = useState(false);
    const [isAddToCategoryModalOpen, setIsAddToCategoryModalOpen] = useState(false);
    const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
    const [editingProductId, setEditingProductId] = useState<number | null>(null);
    const [modalProductIds, setModalProductIds] = useState<number[]>([]);
    const [filtersPopoverOpened, setFiltersPopoverOpened] = useState(false);

    // Фильтры
    const [filterValues, setFilterValues] = useState<ProductFiltersValues>({
        colorIds: [],
        flowerTypeIds: [],
        assemblyTypeIds: [],
        minPrice: '',
        maxPrice: '',
    });
    const [searchQuery, setSearchQuery] = useState('');

    const debouncedSearchQuery = useDebounce(searchQuery, 600);
    const debouncedMinPrice = useDebounce(filterValues.minPrice, 300);
    const debouncedMaxPrice = useDebounce(filterValues.maxPrice, 300);

    // Формируем объект фильтров
    const filters: ProductFilters = useMemo(() => {
        const result: ProductFilters = { categoryId: categoryIdNum };
        if (filterValues.colorIds.length > 0) result.colorIds = filterValues.colorIds;
        if (filterValues.flowerTypeIds.length > 0) result.flowerTypeIds = filterValues.flowerTypeIds;
        if (filterValues.assemblyTypeIds.length > 0) result.assemblyTypeIds = filterValues.assemblyTypeIds;
        if (debouncedMinPrice.trim()) {
            const min = parseFloat(debouncedMinPrice);
            if (!isNaN(min)) result.minPrice = min;
        }
        if (debouncedMaxPrice.trim()) {
            const max = parseFloat(debouncedMaxPrice);
            if (!isNaN(max)) result.maxPrice = max;
        }
        if (debouncedSearchQuery.trim()) {
            result.search = debouncedSearchQuery.trim();
        }
        return result;
    }, [categoryIdNum, filterValues, debouncedMinPrice, debouncedMaxPrice, debouncedSearchQuery]);

    const { data: products = [], isLoading: productsLoading, error: productsError, refetch: refetchProducts } = useProducts(filters);
    const { data: categories = [] } = useCategories();
    const category = categories.find(c => c.id === categoryIdNum);

    const { data: sections = [] } = useSections();
    const section = isPopular || isWithoutSections
        ? null
        : (sectionIdNum
            ? sections.find(s => s.id === sectionIdNum)
            : (category?.sectionId
                ? sections.find(s => s.id === category.sectionId)
                : null));

    const handleClearFilters = () => {
        setFilterValues({
            colorIds: [],
            flowerTypeIds: [],
            assemblyTypeIds: [],
            minPrice: '',
            maxPrice: '',
        });
        setFiltersPopoverOpened(false);
    };

    const handleProductSelect = (productId: number, selected: boolean) => {
        setSelectedProductIds(prev => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(productId);
            } else {
                newSet.delete(productId);
            }
            return newSet;
        });
    };

    const handleBulkActionComplete = () => {
        // Очищаем выбранные товары после выполнения действия
        setSelectedProductIds(new Set());
        // Выключаем режим выбора
        setIsSelectionMode(false);
    };

    const handleOpenAddToSetModal = (productIds: number[]) => {
        setModalProductIds(productIds);
        setIsAddToSetModalOpen(true);
    };

    const handleOpenAddToCategoryModal = (productIds: number[]) => {
        setModalProductIds(productIds);
        setIsAddToCategoryModalOpen(true);
    };

    const handleAddToSetSuccess = () => {
        // Очищаем выбранные товары после выполнения действия
        setSelectedProductIds(new Set());
        // Выключаем режим выбора
        setIsSelectionMode(false);
        // Очищаем ID товаров для модального окна
        setModalProductIds([]);
    };

    const handleAddToCategorySuccess = () => {
        // Очищаем выбранные товары после выполнения действия
        setSelectedProductIds(new Set());
        // Выключаем режим выбора
        setIsSelectionMode(false);
        // Очищаем ID товаров для модального окна
        setModalProductIds([]);
    };

    const handleEditProduct = (productId: number) => {
        setEditingProductId(productId);
        setIsEditProductModalOpen(true);
    };

    const handleEditProductSuccess = () => {
        // Модалка закроется автоматически, список продуктов обновится через query invalidation
        setEditingProductId(null);
    };

    // Формируем breadcrumbs
    const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
        const crumbs: BreadcrumbItem[] = [
            { label: 'Главная', path: '/' },
            { label: 'Категории', path: '/sections' },
        ];

        if (isPopular) {
            crumbs.push({
                label: 'Популярные',
                path: '/sections/popular'
            });
        } else if (isWithoutSections) {
            crumbs.push({
                label: 'Без секции',
                path: '/sections/without-sections'
            });
        } else if (section) {
            crumbs.push({
                label: section.name,
                path: `/sections/${section.id}/categories`
            });
        }

        if (category) {
            crumbs.push({
                label: category.name,
                path: isPopular
                    ? `/sections/popular/categories/${category.id}/products`
                    : isWithoutSections
                        ? `/sections/without-sections/categories/${category.id}/products`
                        : sectionIdNum
                            ? `/sections/${sectionIdNum}/categories/${category.id}/products`
                            : undefined
            });
        }

        return crumbs;
    }, [isPopular, isWithoutSections, section, category, sectionIdNum]);

    return (
        <div className="page-container">
            <Breadcrumbs items={breadcrumbs} />

            <div className="page-header">
                <div className="page-header-top">
                    <h1>Товары {category ? `— ${category.name}` : ''}</h1>
                    {/* <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            variant={isSelectionMode ? 'secondary' : 'primary'}
                            onClick={handleToggleSelectionMode}
                        >
                            {isSelectionMode ? 'Отменить выбор' : 'Выбрать'}
                        </Button>
                    </div> */}
                </div>
            </div>

            <div className="filters-section">
                <div className="search-section">
                    <Input
                        type="text"
                        placeholder="Поиск по названию или ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <FiltersPopover
                    opened={filtersPopoverOpened}
                    onChange={setFiltersPopoverOpened}
                    filterValues={filterValues}
                    onFilterChange={setFilterValues}
                    onClear={handleClearFilters}
                />
                {isSelectionMode && (
                    <BulkActionsSelect
                        selectedProductIds={selectedProductIds}
                        onActionComplete={handleBulkActionComplete}
                        onOpenAddToSetModal={handleOpenAddToSetModal}
                        onOpenAddToCategoryModal={handleOpenAddToCategoryModal}
                    />
                )}
            </div>

            <div className="content-wrapper">
                <ProductList
                    products={products}
                    isSelectionMode={isSelectionMode}
                    selectedProductIds={selectedProductIds}
                    onSelect={handleProductSelect}
                    isLoading={productsLoading}
                    error={productsError}
                    refetch={refetchProducts}
                    searchQuery={debouncedSearchQuery}
                    onEdit={handleEditProduct}
                />
            </div>

            <AddToSetModal
                isOpen={isAddToSetModalOpen}
                onClose={() => {
                    setIsAddToSetModalOpen(false);
                    setModalProductIds([]);
                }}
                productIds={modalProductIds.length > 0 ? modalProductIds : Array.from(selectedProductIds)}
                onSuccess={handleAddToSetSuccess}
            />

            <AddToCategoryModal
                isOpen={isAddToCategoryModalOpen}
                onClose={() => {
                    setIsAddToCategoryModalOpen(false);
                    setModalProductIds([]);
                }}
                productIds={modalProductIds.length > 0 ? modalProductIds : Array.from(selectedProductIds)}
                onSuccess={handleAddToCategorySuccess}
            />

            {editingProductId && (
                <EditProductModal
                    isOpen={isEditProductModalOpen}
                    onClose={() => {
                        setIsEditProductModalOpen(false);
                        setEditingProductId(null);
                    }}
                    productId={editingProductId}
                    onSuccess={handleEditProductSuccess}
                />
            )}
        </div>
    );
}

